import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, ChevronDown, Clock3, Gauge, MapPin, Users } from 'lucide-react';
import { CorporateEventBuilderForm } from '../components/CorporateEventBuilderForm';
import { ContactSection } from '../components/ContactSection';
import { Footer } from '../components/Footer';
import { Navigation } from '../components/Navigation';
import { UtmCapture } from '../components/UtmCapture';
import { teamBuildingActivities, teamBuildingExtras, teamBuildingSteps } from '../data/corporateTeamBuilding';
const trustPoints = ['כתובת אחת שמרכזת פעילות, אוכל ולו״ז', 'התאמה מלאה לאופי ולרמת הקבוצה', 'ליווי והפקה מא׳ ועד ת׳ ביום האירוע'];
const activityCategories = ['אקטיבי', 'רגוע', 'מים', 'תוכן וסדנאות'];
function scrollToBuilder() {
  document.getElementById('event-builder')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
export function CorporateTeamBuildingPage() {
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showMobileCta, setShowMobileCta] = useState(false);
  const formSectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    document.title = "ימי גיבוש לחברות וארגונים | ביץ' קלאב מעגן";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'יום גיבוש על הכנרת — פעילות, אוכל ולו״ז בהתאמה אישית לצוות, עם כתובת אחת שמרכזת את כל היום.');
  }, []);
  useEffect(() => {
    const formSection = formSectionRef.current;
    if (!formSection) return;
    const observer = new IntersectionObserver(([entry]) => {
      setShowMobileCta(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
    }, {
      threshold: 0
    });
    observer.observe(formSection);
    return () => observer.disconnect();
  }, []);
  const visibleActivities = showAllActivities ? teamBuildingActivities : teamBuildingActivities.filter(activity => activity.featured);
  return <div className="min-h-screen bg-white pb-20 md:pb-0" dir="rtl" data-id="element-1003" data-mp="u1xfw5r8axjv">
      <UtmCapture data-id="element-1004" data-mp="u3qohqlejrui" />
      <Navigation data-id="element-1005" data-mp="z0quk1i94ulm" />

      <header className="relative bg-[#0A5F8F] text-white pt-28 pb-14 md:pt-36 md:pb-20 overflow-hidden" data-id="element-1006" data-mp="z1a1yoaa00le">
        <div className="absolute inset-0 opacity-10" aria-hidden="true" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-1007" data-mp="1drtzfofv2ig" />
        <div className="container mx-auto px-4 relative z-10" data-id="element-1008" data-mp="ba4q0k7ghsgd">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-4xl" data-id="element-1009" data-mp="g9zeyi7o87ys">
            <p className="inline-flex items-center gap-2 text-cyan-100 font-semibold mb-5" data-id="element-1010" data-mp="1rnutboqr4coy">
              <Users className="w-5 h-5" data-id="element-1011" data-mp="3c65e1lrwaasr" />
              לחברות, ארגונים וצוותים
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-6 leading-[1.08]" data-id="element-1012" data-mp="379omehlhleax">
              יום גיבוש על הכנרת —
              <span className="block text-cyan-200" data-id="element-1013" data-mp="32fke9v0hj089">
                מתוכנן בדיוק לצוות שלכם
              </span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-blue-50 leading-relaxed mb-7 max-w-3xl text-right" data-id="element-1014" data-mp="2sj7pnbjsrckd">
              פעילות, אוכל ולו״ז מותאם אישית, עם כתובת אחת שמרכזת עבורכם את כל
              היום.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-1015" data-mp="2jjuac28zrtrq">
              <div data-id="element-1016" data-mp="2ek7wbqlca9hv">
                <button type="button" onClick={scrollToBuilder} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-[#0A5F8F] font-bold text-lg py-4 px-7 rounded-xl shadow-lg hover:bg-cyan-50 transition-colors" data-id="element-1017" data-mp="2ekq0rai5nu0v">
                  בואו נתכנן את יום הגיבוש
                  <ArrowLeft className="w-5 h-5" data-id="element-1018" data-mp="36lcnradkwepk" />
                </button>
                <p className="text-sm text-blue-100 mt-2" data-id="element-1019" data-mp="3almwgusr9j84">
                  ללא התחייבות — נחזור אליכם עם האפשרויות המתאימות
                </p>
              </div>
              <span className="inline-flex items-center gap-2 text-blue-100 sm:mr-3" data-id="element-1020" data-mp="2wo7y30kiy140">
                <MapPin className="w-5 h-5 text-cyan-200" data-id="element-1021" data-mp="31jdke0hu9hsm" />
                חוף מעגן, כנרת
              </span>
            </div>
          </motion.div>
        </div>
      </header>

      <main data-id="element-1022" data-mp="2rnu2ii040jmv">
        {/* Early conversion form + trust */}
        <section ref={formSectionRef} id="event-builder" className="py-12 md:py-16 bg-blue-50 scroll-mt-24" data-id="element-1023" data-mp="2dvvyump63fy3">
          <div className="container mx-auto px-4" data-id="element-1024" data-mp="2iu78awbjvofs">
            <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-12 items-start max-w-6xl mx-auto" data-id="element-1025" data-mp="2iuyszc3m01ra">
              

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-white rounded-3xl shadow-xl border border-blue-100 p-5 sm:p-7 md:p-9" data-id="element-1026" data-mp="28wjd65a18ex3">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-1027" data-mp="1z8nkjd0kauhw">
                  בדיקת זמינות וקבלת הצעה
                </h2>
                <p className="text-gray-600 mb-6 text-right" data-id="element-1028" data-mp="1u899dm3nz4fn">
                  שני פרטי חובה בלבד. כל השאר יעזור לנו לדייק את ההצעה.
                </p>
                <CorporateEventBuilderForm data-id="element-1029" data-mp="2489tvbd9rqlf" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Compact activities */}
        <section className="py-14 md:py-16 bg-white" data-id="element-1030" data-mp="2hyn3di5t7dcv">
          <div className="container mx-auto px-4" data-id="element-1031" data-mp="2czts7wdzvyfv">
            <div className="max-w-3xl mb-8" data-id="element-1032" data-mp="2d2etaj6gyack">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F8F] mb-3" data-id="element-1033" data-mp="2rx2sjfla0cjq">
                בוחרים כיוון — אנחנו בונים את השילוב
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed text-right mb-5" data-id="element-1034" data-mp="2mwew2eea33i4">
                אפשר לבנות יום אקטיבי, רגוע או משולב. אין צורך להחליט עכשיו —
                אלה רק האפשרויות שיעזרו לפתוח את השיחה.
              </p>
              <div className="flex flex-wrap gap-2" aria-label="סוגי פעילויות" data-id="element-1035" data-mp="33zdn93unp6mn">
                {activityCategories.map(category => <span key={category} className="bg-blue-50 text-[#075E82] border border-blue-100 rounded-full px-4 py-2 text-sm font-semibold" data-id="element-1036" data-mp="38qvrq1vw1pcd">
                    {category}
                  </span>)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-1037" data-mp="3hph6dly8ok7v">
              {visibleActivities.map((activity, index) => <motion.article key={activity.id} initial={{
              opacity: 0,
              y: 18
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: Math.min(index * 0.04, 0.16)
            }} className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col h-full" data-id="element-1038" data-mp="3rpgfbjykmcl9" data-mpi={index}>
                  {activity.images?.[0] ? <div className="h-48 bg-blue-50 p-2 flex items-center justify-center" data-id="element-1039" data-mp="1l0fs2mkmueyd">
                      <img src={activity.images[0]} alt={activity.title} className="block max-w-full max-h-full object-contain object-center rounded-xl" data-id="element-1040" data-mp="1g360p1dont7w" />
                    </div> : <div className="h-24 bg-blue-50 flex items-center px-6" data-id="element-1041" data-mp="28qifuqhikyr6">
                      <activity.icon className="w-8 h-8 text-[#0A5F8F]" data-id="element-1042" data-mp="28ofrsbzx9fp3" />
                    </div>}
                  <div className="p-5 flex flex-col flex-1" data-id="element-1043" data-mp="23puq0850unyj">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 leading-snug min-h-[3rem]" data-id="element-1044" data-mp="1yrb2426vb23j">
                      {activity.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3" data-id="element-1045" data-mp="nd76ysojn7ww">
                      {activity.description}
                    </p>
                    <dl className="mt-auto space-y-2 border-t border-gray-100 pt-4 text-sm" data-id="element-1046" data-mp="iggv6hjruyha">
                      <div className="flex items-start gap-2" data-id="element-1047" data-mp="1816ug8wi2zs2">
                        <Users className="w-4 h-4 text-[#087EA4] shrink-0 mt-0.5" data-id="element-1048" data-mp="1d2mf3zurn49x" />
                        <dt className="sr-only" data-id="element-1049" data-mp="z2blwq5zhdll">למי מתאים</dt>
                        <dd className="text-gray-700" data-id="element-1050" data-mp="z3wewbcngohc">{activity.audience}</dd>
                      </div>
                      <div className="flex items-start gap-2" data-id="element-1051" data-mp="13589kvadrzwe">
                        <Gauge className="w-4 h-4 text-[#087EA4] shrink-0 mt-0.5" data-id="element-1052" data-mp="1quass7kny0t0" />
                        <dt className="sr-only" data-id="element-1053" data-mp="1vr16io8zwkb9">רמת מאמץ</dt>
                        <dd className="text-gray-700" data-id="element-1054" data-mp="1i0nwmgcjmvja">מאמץ: {activity.effort}</dd>
                      </div>
                      <div className="flex items-start gap-2" data-id="element-1055" data-mp="1m2156y70tctw">
                        <Clock3 className="w-4 h-4 text-[#087EA4] shrink-0 mt-0.5" data-id="element-1056" data-mp="1m06hjc4d76m4" />
                        <dt className="sr-only" data-id="element-1057" data-mp="29qanvr8xtkgt">משך משוער</dt>
                        <dd className="text-gray-700" data-id="element-1058" data-mp="2eitycfcbvzhe">{activity.duration}</dd>
                      </div>
                    </dl>
                  </div>
                </motion.article>)}
            </div>

            <div className="text-center mt-7" data-id="element-1059" data-mp="3uao42nu063wq">
              <button type="button" onClick={() => setShowAllActivities(current => !current)} aria-expanded={showAllActivities} className="inline-flex items-center gap-2 text-[#075E82] border border-[#0A5F8F] font-bold py-3 px-6 rounded-xl hover:bg-blue-50 transition-colors" data-id="element-1060" data-mp="3u7tk0xewil3u">
                {showAllActivities ? 'הסתירו פעילויות נוספות' : 'הציגו פעילויות נוספות'}
                <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${showAllActivities ? 'rotate-180' : ''}`} data-id="element-1061" data-mp="3bcbtcxng0lz1" />
              </button>
            </div>
          </div>
        </section>

        {/* Food and extras — shortened */}
        <section className="py-14 md:py-16 bg-gray-50" data-id="element-1062" data-mp="3lbkhvazjh8e0">
          <div className="container mx-auto px-4" data-id="element-1063" data-mp="15gqy66zdcq2n">
            <div className="grid lg:grid-cols-[1fr_0.9fr] gap-8 lg:gap-12 items-center" data-id="element-1064" data-mp="10iz71f6jt7zi">
              <div data-id="element-1065" data-mp="10lbkbf1d2u68">
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F8F] mb-3" data-id="element-1066" data-mp="3hpouvw26j04f">
                  אוכל, שתייה ופינוקים
                </h2>
                <p className="text-lg text-gray-600 mb-7 text-right" data-id="element-1067" data-mp="3crdm7onmevns">
                  מוסיפים את מה שמתאים לאופי ולשעות של האירוע.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5" data-id="element-1068" data-mp="331c217ye1xe5">
                  {teamBuildingExtras.map(extra => <div key={extra.title} className="flex items-start gap-3" data-id="element-1069" data-mp="2y27pq8g63jjl">
                      <extra.icon className="w-6 h-6 text-[#087EA4] shrink-0" data-id="element-1070" data-mp="2u0m5g21b02zd" />
                      <div data-id="element-1071" data-mp="2u2qj3bp08gcu">
                        <h3 className="font-bold text-gray-900 mb-1" data-id="element-1072" data-mp="2p0rfq1t9ljfw">
                          {extra.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1073" data-mp="2k3ra1s0buipb">
                          {extra.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-1074" data-mp="pum2opi0zizj">
                <img src="https://cdn.magicpatterns.com/uploads/prmJwAT8u35bpB2JwS8Yqu/EliteHenensonPhoto_20260612_Sup_Club_Maagan_363.jpg" alt="מרימים כוסית סביב שולחן האוכל" className="col-span-2 block w-full h-56 object-cover object-center rounded-2xl" data-id="element-1075" data-mp="ptt2k7dknrqd" />
                <img src="https://cdn.magicpatterns.com/uploads/uiWKpHUHiwYM9djQcYMA9f/EliteHenensonPhoto_20260612_Sup_Club_Maagan_352.jpg" alt="מגש כריכים טריים" className="block w-full h-32 object-cover object-center rounded-xl" data-id="element-1076" data-mp="14js3ghwihh9j" />
                <img src="https://cdn.magicpatterns.com/uploads/25RUWhTYYvZktcFSSQd9Cu/EliteHenensonPhoto_20260612_Sup_Club_Maagan_351.jpg" alt="מגש קישים" className="block w-full h-32 object-cover object-center rounded-xl" data-id="element-1077" data-mp="bv37exabc24n" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-[#0A5F8F] text-white" data-id="element-1078" data-mp="1s81k75evbett">
          <div className="container mx-auto px-4 flex flex-col md:flex-row md:items-center md:justify-between gap-5" data-id="element-1079" data-mp="1x7ef8dfw7s7h">
            <div data-id="element-1080" data-mp="3t9zme5pvr80b">
              <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-1081" data-mp="3t9hh6jl8pjg9">
                מוכנים לבדוק מה מתאים לצוות שלכם?
              </h2>
              <p className="text-blue-100" data-id="element-1082" data-mp="3oebuhik0k0r4">
                ללא התחייבות — נחזור אליכם עם האפשרויות המתאימות.
              </p>
            </div>
            <button type="button" onClick={scrollToBuilder} className="inline-flex items-center justify-center gap-2 bg-white text-[#0A5F8F] font-bold py-4 px-7 rounded-xl hover:bg-cyan-50 transition-colors shrink-0" data-id="element-1083" data-mp="25st52lw3qej">
              בואו נתכנן את יום הגיבוש
              <ArrowLeft className="w-5 h-5" data-id="element-1084" data-mp="3jcuwpvwtsuer" />
            </button>
          </div>
        </section>
      </main>

      <ContactSection showTeam={false} showForm={false} data-id="element-1085" data-mp="3emeii9l6l4rc" />
      <Footer data-id="element-1086" data-mp="3ekv4yqqwt7t9" />

      {/* Conversion CTA for mobile Meta traffic */}
      {showMobileCta && <div className="fixed inset-x-0 bottom-0 z-50 md:hidden bg-white border-t border-gray-200 p-3 shadow-[0_-8px_24px_rgba(15,23,42,0.14)]" data-id="element-1087" data-mp="2qur1cjdq1829">
          <button type="button" onClick={scrollToBuilder} className="w-full flex items-center justify-center gap-2 bg-[#0A5F8F] text-white font-bold py-3.5 px-4 rounded-xl" data-id="element-1088" data-mp="2cqi9f3bn32oc">
            בדקו זמינות וקבלת הצעה
            <ArrowLeft className="w-5 h-5" data-id="element-1089" data-mp="22tnbomn6x6b4" />
          </button>
        </div>}
    </div>;
}